import { motion } from "framer-motion";
import { Phone, MapPin, Clock, Star, Building2, Package } from "lucide-react";
import { WIDE_CONTENT_WRAP } from "../constants/layout";
import { useStore } from "../context/StoreContext";

const DAYS_SHORT = [
  { key: "monday", label: "Lun" },
  { key: "tuesday", label: "Mar" },
  { key: "wednesday", label: "Mer" },
  { key: "thursday", label: "Jeu" },
  { key: "friday", label: "Ven" },
  { key: "saturday", label: "Sam" },
  { key: "sunday", label: "Dim" },
];

function formatHours(hours) {
  if (!hours) return null;
  if (typeof hours === "string") return hours;
  if (typeof hours !== "object") return null;
  const weekdays = DAYS_SHORT.slice(0, 5).map((d) => hours[d.key]).filter(Boolean);
  const allSame = weekdays.length > 0 && weekdays.every((h) => h === weekdays[0]);
  if (allSame) {
    let r = `Lun-Ven : ${weekdays[0]}`;
    if (hours.saturday) r += ` · Sam : ${hours.saturday}`;
    if (hours.sunday) r += ` · Dim : ${hours.sunday}`;
    return r;
  }
  return DAYS_SHORT.map((d) => (hours[d.key] ? `${d.label} : ${hours[d.key]}` : null))
    .filter(Boolean)
    .join(" · ");
}

const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } },
};

export function AgenciesSection({ id = "agences", className = "" }) {
  const { agencies } = useStore();

  const storesList = agencies.filter((a) => a.locationType !== "showroom" && !a.isShowroom);
  const showroomsList = agencies.filter((a) => a.locationType === "showroom" || a.isShowroom);
  const allItems = [...storesList, ...showroomsList];
  const total = allItems.length;

  const cardBase =
    "rounded-2xl border p-6 transition-all duration-300 md:p-7";
  const cardStore = `${cardBase} border-gray-200 bg-white shadow-sm shadow-[#190276]/[0.04] hover:-translate-y-0.5 hover:border-[#190276]/25 hover:shadow-md`;
  const cardShowroom = `${cardBase} border-[#190276]/35 bg-gradient-to-br from-[#190276] via-[#1a0a72] to-[#130160] text-white shadow-lg shadow-[#190276]/20 ring-1 ring-white/10 hover:-translate-y-0.5`;

  return (
    <section
      id={id}
      className={`border-t border-gray-200/80 bg-[#eef1f5] py-14 md:py-20 ${className}`.trim()}
      data-testid="agencies-section"
    >
      <div className={WIDE_CONTENT_WRAP}>
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mx-auto mb-10 max-w-3xl text-center md:mb-14"
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#190276]">
            Près de chez vous
          </p>
          <h2 className="font-heading mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Nos agences près de{" "}
            <span className="text-[#f7040a]">chez vous</span>
          </h2>
          <p className="mt-3 text-sm text-gray-600 sm:text-base">
            Retrouvez-nous dans l&apos;une de nos {total} implantations sur la
            Côte d&apos;Azur.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 md:gap-6 lg:grid-cols-3">
          {allItems.map((a, i) => {
            const isShow = a.locationType === "showroom" || a.isShowroom;
            const hoursText = formatHours(a.hours || a.showroomHours);
            return (
              <motion.article
                key={a.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(i * 0.06, 0.36) }}
                className={isShow ? cardShowroom : cardStore}
                data-testid={`agency-${a.id || a.slug}`}
              >
                {a.image && (
                  <div className="mb-5 h-40 overflow-hidden rounded-xl ring-1 ring-black/5">
                    <img
                      src={a.image}
                      alt=""
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        const wrap = e.target.parentElement;
                        if (wrap) wrap.style.display = "none";
                      }}
                    />
                  </div>
                )}
                {isShow && (
                  <div className="mb-4 flex items-center gap-2">
                    <Star
                      className="h-5 w-5 text-[#F2B531]"
                      fill="#F2B531"
                      strokeWidth={2}
                      aria-hidden
                    />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#F2B531]">
                      Showroom
                    </span>
                  </div>
                )}
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${
                      isShow
                        ? "bg-white/15 ring-1 ring-white/20"
                        : "bg-[#190276] shadow-sm"
                    }`}
                  >
                    <Building2
                      className={`h-6 w-6 ${isShow ? "text-[#F2B531]" : "text-white"}`}
                      strokeWidth={2}
                      aria-hidden
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3
                      className={`font-heading text-lg font-bold ${isShow ? "text-white" : "text-gray-900"}`}
                    >
                      {a.name}
                    </h3>
                    <p
                      className={`mt-0.5 text-sm ${isShow ? "text-white/70" : "text-gray-500"}`}
                    >
                      {a.city}
                    </p>
                    <div
                      className={`mt-3 space-y-2 text-sm ${isShow ? "text-white/85" : "text-gray-600"}`}
                    >
                      <div className="flex items-start gap-2">
                        <MapPin
                          className={`mt-0.5 h-4 w-4 shrink-0 ${isShow ? "text-[#F2B531]" : "text-[#190276]"}`}
                          strokeWidth={2}
                          aria-hidden
                        />
                        <span>{a.address}</span>
                      </div>
                      {hoursText && (
                        <div className="flex items-start gap-2">
                          <Clock
                            className={`mt-0.5 h-4 w-4 shrink-0 ${isShow ? "text-[#F2B531]" : "text-gray-400"}`}
                            strokeWidth={2}
                            aria-hidden
                          />
                          <span className="text-xs leading-snug">{hoursText}</span>
                        </div>
                      )}
                      {!isShow && a.supportsClickCollect && (
                        <div className="flex items-center gap-2 pt-1">
                          <Package
                            className="h-3.5 w-3.5 text-[#190276]"
                            strokeWidth={2}
                            aria-hidden
                          />
                          <span className="text-xs font-semibold text-[#190276]">
                            Click &amp; Collect
                          </span>
                        </div>
                      )}
                    </div>
                    {a.phone && (
                      <a
                        href={`tel:${a.phone.replaceAll(" ", "")}`}
                        className={`mt-4 inline-flex items-center gap-2 text-sm font-bold transition-colors ${
                          isShow
                            ? "text-[#F2B531] hover:text-white"
                            : "text-[#190276] hover:text-[#130160]"
                        }`}
                      >
                        <Phone className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
                        {a.phone}
                      </a>
                    )}
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
